/**
 * What a `page_crash` alert says about the crash itself (#7286): the error
 * CLASS, the digest and, for a script that failed to load, the first-party
 * bundle file. Nothing else about the error leaves the browser: no message,
 * no stack, no frames, no `error.data`, no fingerprint built from private
 * text. Readable error text needs a server-side scrub design (#7292).
 */
const SAFE_DIGEST_PATTERN = /^[A-Za-z0-9_-]{1,40}$/;
/** Reads are capped before any matching, so a huge error costs a fixed amount. */
export const CRASH_READ_CAP = 2000;

/** A script or stylesheet file name: letters, digits and `._%~@+-` only. */
const ASSET_FILE = /^[A-Za-z0-9._%~@+-]{1,120}\.(?:m?js|css)$/;
const HOST = /^[A-Za-z0-9.-]{1,120}(?::\d{1,5})?$/;

/**
 * A first-party asset URL reduced to `host/file-name.js`: which of our
 * servers served which bundle file, and nothing else. Directories, query,
 * fragment and userinfo are dropped; a URL on another host, or whose file or
 * host has any odd character, is refused rather than scrubbed.
 */
export function assetHostAndFile(
  url: string | null | undefined,
  firstPartyHost?: string | null
): string | undefined {
  if (typeof url !== 'string' || url.length === 0 || url.length > 800) {
    return undefined;
  }
  const match = /^(?:https?:)?\/\/([^/?#@]*)(\/[^?#]*)?/i.exec(url);
  const host = match?.[1];
  const path = match ? (match[2] ?? '') : url.replace(/[?#][\s\S]*$/, '');
  const file = path.split('/').pop() ?? '';
  if (!ASSET_FILE.test(file)) return undefined;
  if (match) {
    if (!host || !HOST.test(host)) return undefined;
    if (firstPartyHost && host.toLowerCase() !== firstPartyHost.toLowerCase()) {
      return undefined;
    }
  }
  return `${host ?? ''}${host ? '/' : ''}${file}`;
}

function readString(error: unknown, key: 'name' | 'message'): string {
  if (typeof error === 'string') return key === 'message' ? error : '';
  if (typeof error !== 'object' || error === null) return '';
  const value = (error as Record<string, unknown>)[key];
  return typeof value === 'string' ? value : '';
}

/**
 * The error classes a crash may name. An error's `name` is a mutable string a
 * custom error can fill from user data, so it is never copied: it is matched
 * EXACTLY against this finite set, and anything else (a custom class, an
 * identifier-shaped private value) becomes the fixed fallback.
 */
export const KNOWN_ERROR_CLASSES: ReadonlySet<string> = new Set([
  'Error',
  'TypeError',
  'SyntaxError',
  'ReferenceError',
  'RangeError',
  'EvalError',
  'URIError',
  'AggregateError',
  'ChunkLoadError',
  'ConvexError',
  'UserBlockingAlertProbeError',
]);
export const UNKNOWN_ERROR_CLASS = 'UnknownError';

/** The class of the error: a known literal, else the fixed fallback. */
export function crashErrorClass(error: unknown): string {
  const name = readString(error, 'name');
  return KNOWN_ERROR_CLASSES.has(name) ? name : UNKNOWN_ERROR_CLASS;
}

export function crashErrorDigest(error: unknown): string | undefined {
  const digest = (error as { digest?: unknown } | null)?.digest;
  return typeof digest === 'string' && SAFE_DIGEST_PATTERN.test(digest)
    ? digest
    : undefined;
}

const CHUNK_FAILURE_PATTERNS: readonly RegExp[] = [
  /ChunkLoadError/,
  /Loading (?:CSS )?chunk [\w-]+ failed/,
  /Failed to load chunk/,
  /Failed to fetch dynamically imported module/,
  /error loading dynamically imported module/,
  /Importing a module script failed/,
];
const CHUNK_URL_IN_MESSAGE =
  /(?:https?:)?\/\/[^\s()'"]{1,300}\.(?:m?js|css)(?=[?#\s)'"]|$)|\/_next\/static\/[^\s()'"?#]{1,300}\.(?:m?js|css)/;

/**
 * The failing bundle file, only when the error IS a script/chunk load
 * failure and names a first-party asset: `host/file.js`, no query or hash.
 */
export function crashScriptFile(
  error: unknown,
  firstPartyHost?: string | null
): string | undefined {
  const message = readString(error, 'message').slice(0, CRASH_READ_CAP);
  const isChunkFailure =
    readString(error, 'name') === 'ChunkLoadError' ||
    CHUNK_FAILURE_PATTERNS.some((pattern) => pattern.test(message));
  if (!isChunkFailure) return undefined;
  const url = CHUNK_URL_IN_MESSAGE.exec(message)?.[0];
  if (!url) return undefined;
  // A path-only URL is this site's own.
  return assetHostAndFile(
    url.startsWith('/_next') && firstPartyHost
      ? `//${firstPartyHost}${url}`
      : url,
    firstPartyHost
  );
}

/** The only operations a crash may name: the chunk-load fallback outcomes. */
const CRASH_OPERATION_PATTERN =
  /^chunk_load_fallback\/(?:repeat|unsafe|storage)$/;

/**
 * The whole crash event, as properties: the error class, digest, route
 * pattern and (for a script/chunk load failure) the first-party bundle file.
 * Built from scratch for the independent capture, never from an SDK event,
 * so nothing the SDK knows (person or session properties, persisted initial
 * values, page URL, referrer) can be in it. `probe` marks the alert probe.
 */
export interface PageCrashProperties {
  surface: 'page_crash';
  operation: string;
  error_kind: 'client_error';
  error_code: string;
  error_name: string;
  error_digest?: string;
  route_pattern?: string;
  script_url?: string;
  alert_key: string;
  probe?: true;
}

export function buildPageCrashProperties(
  error: unknown,
  context: {
    operation?: string;
    routePattern?: string;
    firstPartyHost?: string | null;
  } = {}
): PageCrashProperties {
  const errorClass = crashErrorClass(error);
  const digest = crashErrorDigest(error);
  const script = crashScriptFile(error, context.firstPartyHost);
  const operation =
    context.operation && CRASH_OPERATION_PATTERN.test(context.operation)
      ? context.operation
      : 'unknown';
  return {
    surface: 'page_crash',
    operation,
    error_kind: 'client_error',
    error_code: errorClass,
    error_name: errorClass,
    ...(digest ? { error_digest: digest } : {}),
    ...(context.routePattern ? { route_pattern: context.routePattern } : {}),
    ...(script ? { script_url: script } : {}),
    // Unrelated crashes share a class: split them by route and render digest.
    alert_key: [
      'page_crash',
      operation,
      errorClass,
      context.routePattern?.split('/')[2],
      digest,
    ]
      .filter(Boolean)
      .join(':'),
    ...(errorClass === 'UserBlockingAlertProbeError'
      ? { probe: true as const }
      : {}),
  };
}
